import { useContext, useMemo } from 'react';
import ColorModeContext from '../layout/themeContext';
import { 
  iconColors, 
  semanticColors, 
  IconCategory, 
  StatusIndicator,
  ThemeMode 
} from '../constants/iconColors';

export interface UseColoredIconReturn {
  color: string;
  hoverColor: string;
  semanticColor?: string;
  isDarkMode: boolean;
}

/**
 * Hook para gerenciar cores de ícones baseado no tema e categoria
 * 
 * @param category - Categoria do ícone (dashboard, tickets, contacts, etc)
 * @param statusIndicator - Indicador de status opcional (success, error, warning, info)
 * @returns Objeto com cores resolvidas e informações de tema
 * 
 * @example
 * const { color, hoverColor, isDarkMode } = useColoredIcon('dashboard');
 */
export function useColoredIcon(
  category: IconCategory,
  statusIndicator?: StatusIndicator
): UseColoredIconReturn {
  const colorModeContext = useContext(ColorModeContext);
  
  // Obter o modo de tema do contexto
  const mode = colorModeContext?.mode || 'light';
  const isDarkMode = mode === 'dark';

  return useMemo(() => {
    try {
      // Validar categoria
      if (!iconColors[category]) {
        console.warn(
          `Categoria de ícone inválida: "${category}". Usando "dashboard" como fallback.`
        );
        category = 'dashboard';
      }

      const categoryColors = iconColors[category];
      
      // Resolver cores baseado no tema
      const color = isDarkMode ? categoryColors.dark : categoryColors.light;
      const hoverColor = isDarkMode 
        ? categoryColors.darkHover 
        : categoryColors.lightHover;

      // Resolver cor semântica se fornecida
      let semanticColor: string | undefined;
      if (statusIndicator && statusIndicator !== 'none') {
        if (semanticColors[statusIndicator]) {
          semanticColor = isDarkMode
            ? semanticColors[statusIndicator].dark
            : semanticColors[statusIndicator].light;
        }
      }

      return {
        color,
        hoverColor,
        semanticColor,
        isDarkMode
      };
    } catch (error) {
      console.error('Erro ao resolver cores do ícone:', error);
      // Fallback para cores padrão
      return {
        color: isDarkMode ? '#64B5F6' : '#1976D2',
        hoverColor: isDarkMode ? '#90CAF9' : '#1565C0',
        isDarkMode
      };
    }
  }, [category, statusIndicator, isDarkMode]);
}
